import type { ReactNode } from 'react';
import { CEW_FOOTER_LINKS, CEW_SOCIAL_LINKS, CEW_LINK_TARGET } from '../../lib/cew-site';

/**
 * CewSiteFooter (CR-030) — replaces BOTH the old slim `Footer.tsx`
 * and the marketing `HomePageFooter.tsx` (Rule 18 removal
 * completeness: both are deleted in the same change). Replicates
 * cew.georgetown.edu's global footer band — About CEW / Privacy
 * Policy / EIT accessibility links + 5 social icons — plus a slim
 * navy credit strip carrying the funder mention (our one addition,
 * spec section 2.3; the funder credit lived only in the footers this
 * removes, and the tool is a grant deliverable).
 *
 * Shared across Home / Rankings / Detail / Compare / Providers (Map
 * stays footer-less — full-viewport page).
 */

interface SocialIcon {
  readonly viewBox: string;
  readonly node: ReactNode;
}

// Reused from the old HomePageFooter's SOCIAL_LINKS icon paths.
// LinkedIn swapped to the square "fa-linkedin-square" glyph per the
// parity spec (CEW renders the square variant, not the bare "in" mark).
const SOCIAL_ICONS: Record<string, SocialIcon> = {
  X: {
    viewBox: '0 0 24 24',
    node: (
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.91-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    ),
  },
  Instagram: {
    viewBox: '0 0 24 24',
    node: (
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
      </g>
    ),
  },
  LinkedIn: {
    viewBox: '0 0 448 512',
    node: (
      <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z" />
    ),
  },
  YouTube: {
    viewBox: '0 0 24 24',
    node: (
      <>
        <path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58 2.78 2.78 0 001.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.96A29 29 0 0023 12a29 29 0 00-.46-5.58z" />
        {/* Play-button cutout: a fixed navy fill against the gold glyph
            body (the svg's own fill="currentColor") -- a Tailwind fill
            utility on the theme color, never a raw hex in JSX. */}
        <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" className="fill-navy" />
      </>
    ),
  },
  Medium: {
    viewBox: '0 0 24 24',
    node: (
      <path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zM20.96 12c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z" />
    ),
  },
};

export function CewSiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-navy text-white" role="contentinfo">
      <div className="flex flex-col items-center gap-y-[10px] px-[6vw] py-[27px] min-[1001px]:grid min-[1001px]:grid-cols-[25%_50%_25%] min-[1001px]:items-center min-[1001px]:gap-y-0 min-[1001px]:py-[4vw]">
        {/* Col 1 — empty on CEW's own site (reserved space, nothing to copy). */}
        <div aria-hidden="true" className="hidden min-[1001px]:block" />

        {/* CR-030 3rd code-review pass (2026-08-18): Alfa sia-r113 flagged
            the tablet/Providers screenshot — px-[10px] padding on each
            link put visible whitespace around the TEXT but left the link
            BOXES touching (0px between them), since padding lives inside
            an element's own box. gap-x-2 (8px) + px-1.5 (6px) reproduces
            CEW's ~20px measured word spacing (6 + 8 + 6) while giving the
            boxes themselves a real >=8px gap. */}
        <nav aria-label="Footer links" className="flex flex-wrap items-center justify-center gap-x-2">
          {CEW_FOOTER_LINKS.map((link) => (
            <a
              key={link.id}
              href={link.href}
              target={CEW_LINK_TARGET}
              rel="noopener"
              className="inline-flex min-h-6 items-center px-1.5 text-lg font-medium text-white underline decoration-1 underline-offset-[6px]"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <ul aria-label="Social media links" className="flex justify-center min-[1001px]:justify-end">
          {CEW_SOCIAL_LINKS.map((social) => {
            const icon = SOCIAL_ICONS[social.label];
            return (
              <li key={social.id}>
                <a
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="flex h-12 w-12 items-center justify-center text-gold"
                >
                  <svg width={28} height={28} viewBox={icon?.viewBox ?? '0 0 24 24'} fill="currentColor" aria-hidden="true">
                    {icon?.node}
                  </svg>
                </a>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Our one addition (spec 2.3) — funder credit strip below the CEW
          band, in the same navy, so nothing above it shifts. */}
      <div className="border-t border-white/10 px-[6vw] py-3 text-center text-sm text-white/70">
        &copy; {year} Georgetown University Center on Education and the Workforce &middot; Funded by
        Ascendium Education Group
      </div>
    </footer>
  );
}
